<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="500"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        x-small
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Subordinates"
      text="Update subordinates"
      class="mb-0"
      :loading="loading"
    >
      <form v-if="dialog && !loading">
        <v-container>
          <v-row>
            <v-col
              cols="12"
              class="pb-0"
            >
              <v-autocomplete
                :value="subordinatesLeft"
                :items="subordinates"
                :label="'Current Subordinates'"
                hide-no-data
                disabled
                hide-selected
                item-text="employee.person.name"
                multiple
                chips
                outlined
                return-object
              >
                <template v-slot:selection="data">
                  <v-chip>
                    {{ data.item.employee.person.name }}
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-col> 

            <v-col           
              cols="12"
              class="pb-0"
            >
              <v-autocomplete
                v-model="selectedSubordinates"
                v-validate="'required'"
                :items="subordinates"
                :label="'Subordinates to Transfer'"
                :placeholder="'Subordinate Names'"
                clearable
                hide-no-data
                hide-selected
                item-text="employee.person.name"
                :error-messages="errors.collect('selectedSubordinates')"
                data-vv-name="selectedSubordinates"  
                data-vv-as="subordinate to transfer" 
                multiple
                chips
                return-object
                outlined
                deletable-chips
              /> 
            </v-col>

            <v-col 
              cols="12"
              class="pb-0"
            >
              <v-autocomplete
                v-model="newSubordinates"
                v-validate="'required'"
                :items="employees"
                item-text="person.name"
                item-value="employee_uid"
                :error-messages="errors.collect('newSubordinates')"
                data-vv-name="newSubordinates"  
                data-vv-as="subordinate new superior" 
                label="Subordinates New Superior"
                outlined
              />
            </v-col>

            <v-col
              cols="12"
              class="pb-0"
            >
              <v-menu
                v-model="effectiveDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="effectiveDate"
                    v-validate="'required'"
                    label="Effective Date"
                    :error-messages="errors.collect('effectiveDate')"
                    data-vv-name="effectiveDate"  
                    data-vv-as="effective date"      
                    readonly
                    class="fix-date-picker-outline-overlap"
                    outlined
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="effectiveDate"
                  @input="effectiveDateMenu = false"
                />
              </v-menu>

              <p
                v-if="$moment(effectiveDate).diff($moment()) >= 0"
                class="warning--text"
              >
                By selecting a future date, the details will only save when it reach the date
              </p>
            </v-col>
          </v-row>
        </v-container>
      </form> 

      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"    
          class="white--text px-10"      
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions> 
    </material-card>  
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employeeUid: {
        type: String,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        loading: false,
        subordinates: [],
        selectedSubordinates: [],
        newSubordinates: null,
        employees: [],
        effectiveDateMenu: null,
        effectiveDate: null,
      }
    },
    computed: {
      subordinatesLeft () {
        return this.subordinates.filter((subordinate) => {
          let index = this.selectedSubordinates.findIndex(selected => selected.employee_uid === subordinate.employee_uid)
          if (index >= 0) {
            return false
          } else {
            return true
          }
        })
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return this.$resetData()

        this.loading = true

        Promise.all([
          this.$api.hrisEmployees().list('?with=vessel'),
          this.$api.hrisSubordinates().list(this.employeeUid),
        ]).then(([employee, subordinate]) => {
          this.employees = employee.data.filter(employee => {
            return employee.employee_uid !== this.employeeUid
          })
          this.employees = _.orderBy(employee.data, ['person.name'], ['asc'])
                    
          this.subordinates = subordinate.data
   
          this.loading = false
        })
      },
    },
    methods: { 
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.setProgressing(true)

            this.$api.bulkCreateReportTo().bulkCreate(this.prepareFormData())
              .then(response => {
                if (response.status === 200 || response.status === 201) {
                  this.$emit('updated')
                  this.$snack({ message: response.data.status })

                  this.setProgressing(false)
                  this.dialog = false
                }
              }).catch(error => {
                if (error.response.status === 405) {
                  this.$emit('updated')
                  this.setProgressing(false)
                  this.dialog = false
                  
                  this.$dialog({
                    title: 'Unable to change reporting line',
                    message: error.response.data.message, 
                    color: 'error',
                  })
                  this.setProgressing(false)
                }
              })
          })
      },
      prepareFormData () {
        return {
          effective_date: this.effectiveDate,
          changes: this.selectedSubordinates.map(subordinate => {
            return {
              employee_uid: subordinate.employee_uid,
              assigned_to: this.newSubordinates,
            }
          }),
        }
      },
      remove (item) {
        let index = this.selectedSubordinates.findIndex(selected => selected.employee_uid === item.employee_uid)
        if (index >= 0) this.selectedSubordinates.splice(index, 1)
      },
      setProgressing (status) {
        this.progressing = status
      },
    },
  }
</script>

<style scoped>
.fix-date-picker-outline-overlap >>> label{
  background: white;
  padding-right:10px
}
</style>
